import React from "react";
import { useTranslation } from "react-i18next";
import {
    Actions,
    Box,
    Button,
    Column,
    Columns,
    Drawer,
    Stack,
    TextField,
} from "braid-design-system";
import { useToast } from "../../hooks/useToast";
import NumericTextField from "../NumericTextField";
import EmojiPickerField from "./EmojiPickerField";
import GroupImageField from "./GroupImageField";
import { uploadResourceFile } from "../../services/itemResourceService";

// Matches GROUP_UPLOAD_PREFIX in backend/app/services/pos_item_group_service.py —
// only files with this prefix are cleaned up when a group image is replaced.
const GROUP_UPLOAD_PREFIX = "GRP_";

export interface ItemGroupFormData {
    group_code: string;
    group_name: string;
    group_icon: string;
    group_image: string;
    group_saccno: string;
    group_caccno: string;
    group_iaccno: string;
    group_prchadays: string;
    group_reorder: string;
}

export type ItemGroupToEdit = {
    group_id?: number | null;
} & Partial<{
    [K in keyof ItemGroupFormData]: ItemGroupFormData[K] | null;
}>;

interface FormErrors {
    group_code?: string;
    group_name?: string;
    group_icon?: string;
    group_image?: string;
    group_saccno?: string;
    group_caccno?: string;
    group_iaccno?: string;
    group_prchadays?: string;
    group_reorder?: string;
}

interface CreateUpdateItemGroupDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    itemGroupToEdit?: ItemGroupToEdit;
    onSave: (groupData: ItemGroupFormData, editingGroup?: ItemGroupToEdit) => Promise<void> | void;
    onItemGroupSaved?: () => void;
    isReadOnly?: boolean;
}

const defaultFormData: ItemGroupFormData = {
    group_code: "",
    group_name: "",
    group_icon: "",
    group_image: "",
    group_saccno: "",
    group_caccno: "",
    group_iaccno: "",
    group_prchadays: "",
    group_reorder: "",
};

const CreateUpdateItemGroupDrawer: React.FC<CreateUpdateItemGroupDrawerProps> = ({
    isOpen,
    onClose,
    itemGroupToEdit,
    onSave,
    onItemGroupSaved,
    isReadOnly = false,
}) => {
    const { t } = useTranslation();
    const toast = useToast();

    const [formData, setFormData] = React.useState<ItemGroupFormData>(defaultFormData);
    const [errors, setErrors] = React.useState<FormErrors>({});
    const [submitting, setSubmitting] = React.useState(false);
    const [selectedImage, setSelectedImage] = React.useState<File | null>(null);

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (itemGroupToEdit) {
            setFormData({
                group_code: itemGroupToEdit.group_code ?? "",
                group_name: itemGroupToEdit.group_name ?? "",
                group_icon: itemGroupToEdit.group_icon ?? "",
                group_image: itemGroupToEdit.group_image ?? "",
                group_saccno: itemGroupToEdit.group_saccno ?? "",
                group_caccno: itemGroupToEdit.group_caccno ?? "",
                group_iaccno: itemGroupToEdit.group_iaccno ?? "",
                group_prchadays: itemGroupToEdit.group_prchadays != null ? String(itemGroupToEdit.group_prchadays) : "",
                group_reorder: itemGroupToEdit.group_reorder != null ? String(itemGroupToEdit.group_reorder) : "",
            });
        } else {
            setFormData(defaultFormData);
        }

        setErrors({});
        setSelectedImage(null);
    }, [isOpen, itemGroupToEdit]);

    const updateText = (key: keyof ItemGroupFormData, value: string) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
        if (errors[key as keyof FormErrors]) {
            setErrors((prev) => ({ ...prev, [key]: undefined }));
        }
    };

    const updateNumber = (key: keyof ItemGroupFormData, value: string) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
        if (errors[key as keyof FormErrors]) {
            setErrors((prev) => ({ ...prev, [key]: undefined }));
        }
    };

    const validate = () => {
        const nextErrors: FormErrors = {};

        if (!(formData.group_code ?? "").trim()) {
            nextErrors.group_code = t("page.item_group_management.drawer.validation.required", { defaultValue: "Required" });
        }

        if (!(formData.group_name ?? "").trim()) {
            nextErrors.group_name = t("page.item_group_management.drawer.validation.required", { defaultValue: "Required" });
        }

        if (formData.group_code.length > 3) {
            nextErrors.group_code = t("page.item_group_management.drawer.validation.max_length", {
                defaultValue: "Maximum 3 characters allowed",
            });
        }

        if (formData.group_name.length > 40) {
            nextErrors.group_name = t("page.item_group_management.drawer.validation.max_length", {
                defaultValue: "Maximum 40 characters allowed",
            });
        }

        if ((formData.group_icon ?? "").trim() !== "" && [...(formData.group_icon ?? "").trim()].length > 4) {
            nextErrors.group_icon = t("page.item_group_management.drawer.validation.icon_invalid", {
                defaultValue: "Enter a single emoji",
            });
        }

        if (formData.group_prchadays !== "") {
            const numValue = parseInt(formData.group_prchadays, 10);
            if (isNaN(numValue) || numValue < 0) {
                nextErrors.group_prchadays = t("page.item_group_management.drawer.validation.prchadays_invalid", {
                    defaultValue: "Must be 0 or a positive number",
                });
            }
        }

        if (formData.group_reorder !== "") {
            const numValue = parseInt(formData.group_reorder, 10);
            if (isNaN(numValue) || numValue < 0) {
                nextErrors.group_reorder = t("page.item_group_management.drawer.validation.reorder_invalid", {
                    defaultValue: "Must be 0 or a positive number",
                });
            }
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (isReadOnly) {
            return;
        }

        if (submitting) {
            return;
        }

        if (!validate()) {
            toast.showError(t("page.item_group_management.drawer.validation.fix_errors", { defaultValue: "Please fix validation errors" }));
            return;
        }

        try {
            setSubmitting(true);

            let dataToSave = formData;
            if (selectedImage) {
                const imagePath = await uploadResourceFile(
                    selectedImage,
                    "image",
                    `${GROUP_UPLOAD_PREFIX}${(formData.group_code ?? "").trim()}`,
                );
                dataToSave = { ...formData, group_image: imagePath };
            }

            await onSave(dataToSave, itemGroupToEdit);

            if (onItemGroupSaved) {
                onItemGroupSaved();
            }

            toast.showSuccess(
                itemGroupToEdit
                    ? t("page.item_group_management.drawer.success.updated", { defaultValue: "Item group updated successfully" })
                    : t("page.item_group_management.drawer.success.created", { defaultValue: "Item group created successfully" }),
            );

            onClose();
        } catch (error: any) {
            toast.showError(error?.message || t("page.item_group_management.drawer.error.save_failed", { defaultValue: "Failed to save item group" }));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Drawer
            open={isOpen}
            onClose={onClose}
            title={
                itemGroupToEdit
                    ? t("page.item_group_management.drawer.title.update", { defaultValue: "Update Item Group" })
                    : t("page.item_group_management.drawer.title.create", { defaultValue: "Create New Item Group" })
            }
            width="medium"
        >
            <form onSubmit={handleSubmit} autoComplete="off">
                <Box padding="large">
                    <Stack space="large">
                        <Columns space="small" collapseBelow="desktop">
                            <Column width="1/2">
                                <Stack space="large">
                                    <TextField
                                        label={t("page.item_group_management.drawer.label.group_code", { defaultValue: "Group Code*" })}
                                        value={formData.group_code}
                                        tone={errors.group_code ? "critical" : undefined}
                                        message={errors.group_code}
                                        onChange={(e) => updateText("group_code", e.currentTarget.value)}
                                    />
                                    <TextField
                                        label={t("page.item_group_management.drawer.label.group_name", { defaultValue: "Group Name*" })}
                                        value={formData.group_name}
                                        tone={errors.group_name ? "critical" : undefined}
                                        message={errors.group_name}
                                        onChange={(e) => updateText("group_name", e.currentTarget.value)}
                                    />
                                    <EmojiPickerField
                                        label={t("page.item_group_management.drawer.label.group_icon", { defaultValue: "Icon (emoji)" })}
                                        value={formData.group_icon}
                                        tone={errors.group_icon ? "critical" : undefined}
                                        message={errors.group_icon}
                                        disabled={isReadOnly}
                                        onChange={(value) => updateText("group_icon", value)}
                                    />
                                    <GroupImageField
                                        label={t("page.item_group_management.drawer.label.group_image", { defaultValue: "Marketplace Image" })}
                                        description={t("page.item_group_management.drawer.hint.group_image", {
                                            defaultValue: "Shown as this category's tile on the marketplace. If empty, a product photo from this group is used.",
                                        })}
                                        currentPath={formData.group_image}
                                        selectedFile={selectedImage}
                                        disabled={isReadOnly}
                                        onSelect={setSelectedImage}
                                        onRemove={() => {
                                            setSelectedImage(null);
                                            updateText("group_image", "");
                                        }}
                                    />
                                    <NumericTextField
                                        label={t("page.item_group_management.drawer.label.group_reorder", { defaultValue: "Reorder Point" })}
                                        value={formData.group_reorder}
                                        mode="integer"
                                        tone={errors.group_reorder ? "critical" : undefined}
                                        message={errors.group_reorder}
                                        onChange={(value) => updateNumber("group_reorder", value)}
                                        placeholder="0"
                                    />
                                </Stack>
                            </Column>
                            <Column width="1/2">
                                <Stack space="large">
                                    <TextField
                                        label={t("page.item_group_management.drawer.label.group_saccno", { defaultValue: "Sales Account No" })}
                                        value={formData.group_saccno}
                                        tone={errors.group_saccno ? "critical" : undefined}
                                        message={errors.group_saccno}
                                        onChange={(e) => updateText("group_saccno", e.currentTarget.value)}
                                    />
                                    <TextField
                                        label={t("page.item_group_management.drawer.label.group_caccno", { defaultValue: "COS Account No" })}
                                        value={formData.group_caccno}
                                        tone={errors.group_caccno ? "critical" : undefined}
                                        message={errors.group_caccno}
                                        onChange={(e) => updateText("group_caccno", e.currentTarget.value)}
                                    />
                                    <TextField
                                        label={t("page.item_group_management.drawer.label.group_iaccno", { defaultValue: "Inventory Account No" })}
                                        value={formData.group_iaccno}
                                        tone={errors.group_iaccno ? "critical" : undefined}
                                        message={errors.group_iaccno}
                                        onChange={(e) => updateText("group_iaccno", e.currentTarget.value)}
                                    />
                                    <NumericTextField
                                        label={t("page.item_group_management.drawer.label.group_prchadays", { defaultValue: "Group Price History Days" })}
                                        value={formData.group_prchadays}
                                        mode="integer"
                                        tone={errors.group_prchadays ? "critical" : undefined}
                                        message={errors.group_prchadays}
                                        onChange={(value) => updateNumber("group_prchadays", value)}
                                        placeholder="0"
                                    />
                                </Stack>
                            </Column>
                        </Columns>

                        <Box display="flex" justifyContent="flexEnd">
                            <Actions>
                                <Button type={isReadOnly ? "button" : "submit"} variant="ghost" tone="brandAccent">
                                    {isReadOnly
                                        ? t("page.item_group_management.drawer.submit.view_only", { defaultValue: "View Only" })
                                        : submitting
                                            ? t("page.item_group_management.drawer.submit.saving", { defaultValue: "Saving..." })
                                            : itemGroupToEdit
                                                ? t("page.item_group_management.drawer.submit.update", { defaultValue: "Update" })
                                                : t("page.item_group_management.drawer.submit.create", { defaultValue: "Create" })}
                                </Button>
                            </Actions>
                        </Box>
                    </Stack>
                </Box>
            </form>
        </Drawer>
    );
};

export default CreateUpdateItemGroupDrawer;